import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface CookieConsentCategories {
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentRecord {
  v: string;
  ts: string;
  c: CookieConsentCategories;
}

const COOKIE_NAME = 'convivia_consent';
const POLICY_VERSION = '1.0';
const COOKIE_MAX_AGE_DAYS = 365;

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  readonly showBanner = signal(false);
  readonly categories = signal<CookieConsentCategories>({ analytics: false, marketing: false });

  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.init();
  }

  acceptAll(): void {
    this.save({ analytics: true, marketing: true });
  }

  rejectNonEssential(): void {
    this.save({ analytics: false, marketing: false });
  }

  openPreferences(): void {
    this.showBanner.set(true);
  }

  private init(): void {
    if (!this.isBrowser) return;

    const raw = this.readCookie(COOKIE_NAME);
    if (!raw) {
      this.showBanner.set(true);
      return;
    }

    try {
      const record: CookieConsentRecord = JSON.parse(decodeURIComponent(raw));
      if (record.v !== POLICY_VERSION) {
        this.showBanner.set(true);
        return;
      }
      this.categories.set(record.c);
    } catch {
      this.showBanner.set(true);
    }
  }

  private save(cats: CookieConsentCategories): void {
    if (!this.isBrowser) return;

    const record: CookieConsentRecord = {
      v: POLICY_VERSION,
      ts: new Date().toISOString(),
      c: cats,
    };

    const value = encodeURIComponent(JSON.stringify(record));
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_MAX_AGE_DAYS);
    document.cookie = `${COOKIE_NAME}=${value}; expires=${expires.toUTCString()}; path=/; Secure; SameSite=Strict`;

    this.categories.set(cats);
    this.showBanner.set(false);
  }

  private readCookie(name: string): string | null {
    const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
    return match ? match[1] : null;
  }
}
